import { Country, State } from "country-state-city";
import React, { useState, useEffect } from "react";
import { Form, Button, message } from "antd";
import { useSelector, useDispatch } from 'react-redux';
import postalCodes from "postal-codes-js";
import { 
  SUPPLIER_STATUS, 
  VALIDATION_RULES, 
  ERROR_MESSAGES, 
  TEXTS, 
  SUPPLIER_DETAILS_CONSTANTS 
} from "../../constants/Constants";
import FormInput from "../common/FormInput/FormInput";
import { 
  setCompanyName,
  setAddressLine1,
  setAddressLine2,
  setCountry, 
  setState, 
  setZipCode, 
  setIsExistingCompany,
  setSupplierType, 
  resetSignup, 
  setUserType,
  setIsErrorSupplierDetails
} from '../../redux/slices/signupSlice';
import axios from "axios";
import { decimalCheck, specialCharacterCheck, allowedSpecialCharacterCheck } from "../../util/validation";
import { useNavigate } from "react-router-dom";

const SupplierDetails = ({ onAdd, clearData, initialValues }) => {
  const [countryCode, setCountryCode] = useState();
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [hasStates, setHasStates] = useState(true);
  const dispatch = useDispatch();
  const [form] = Form.useForm();

  const [supplierErrors, setSupplierErrors] = useState({});

  const {
    companyName,
    addressLine1,
    addressLine2,
    supplierType,
    country,
    state,
    zipcode,
    existingSupplier,
    isExistingCompany
  } = useSelector((state) => state.signup);
   const navigate=useNavigate();

  useEffect(() => {
    setCountries(Country.getAllCountries());
    setStates(State.getStatesOfCountry(country))
  }, []);


  useEffect(() => {
    const hasErrors = Object.keys(supplierErrors).length > 0;
    dispatch(setIsErrorSupplierDetails(hasErrors));
    console.log(hasErrors)
  }, [supplierErrors, dispatch]);

 
  const handleFieldError = (fieldName, error) => {
    if (error) {
      setSupplierErrors(prev => ({
        ...prev,
        [fieldName]: error
      }));
    } else {
      setSupplierErrors(prev => {
        const updated = { ...prev };
        delete updated[fieldName];
        return updated;
      });
    }
  };


  const validateCompanyNameAsync = async (_, value) => {
    if (!value) return Promise.resolve();
    if (existingSupplier) return Promise.resolve();

    try {
      const response = await axios.get(`https://techsolutionsservice.ustdev.com/signup/validateCompany/${value}`);
      if (response.data) {
        const errorMessage = response.data;
        handleFieldError('companyName', errorMessage);
        dispatch(setIsExistingCompany(true));
        dispatch(resetSignup());
        

        setTimeout(() => {
          form.setFieldsValue({
            addressLine1: "",
            addressLine2: "",
            supplierType: undefined,
            country: undefined,
            state: undefined,
            zipcode: undefined,
          });
        }, 0);

        return Promise.reject(new Error(errorMessage));
      }
      

      handleFieldError('companyName', null);
      dispatch(setIsExistingCompany(false));
      return Promise.resolve();
    } catch (error) {
      const errorMessage = error.message || "Error validating company name";
      handleFieldError('companyName', errorMessage);
      return Promise.reject(new Error(errorMessage));
    }
  };

  const backToLogin=()=>{
    navigate("/supplierLogin")
  }


  const onFinish = (values) => {

    setSupplierErrors({});
    dispatch(setIsErrorSupplierDetails(false));
    
    onAdd(values);
  };


  const onCancel = () => {

    setSupplierErrors({});
    dispatch(setIsErrorSupplierDetails(false));


    dispatch(setCompanyName(""));
    dispatch(setAddressLine1(""));
    dispatch(setAddressLine2(""));
    dispatch(setUserType(undefined));
    dispatch(setCountry(undefined));
    dispatch(setState(undefined));
    dispatch(setZipCode(""));

    form.resetFields();

    if (clearData) {
      clearData();
    }
  };
  

  const handleCountryChange = (value) => {
    setCountryCode(value);
    dispatch(setCountry(value));
    
  
    dispatch(setState(undefined));
    dispatch(setZipCode(""));

    
    const countryStates = State.getStatesOfCountry(value);

    
    setStates(countryStates);
    setHasStates(countryStates.length > 0);
    
    form.setFieldsValue({ 
      state: countryStates.length === 0 ? 'N/A' : undefined,
      zipcode: ''
    });

    if (countryStates.length === 0) {
      dispatch(setState('N/A'));
    }
  };

  return (
    <Form 
      form={form} 
      layout="vertical" 
      onFinish={onFinish}
      onFieldsChange={(changedFields) => {
        changedFields.forEach(field => {
          if (field.errors && field.errors.length > 0) {
            handleFieldError(field.name[0], field.errors[0]);
          } else {
            handleFieldError(field.name[0], null);
          }
        });
      }}
      initialValues={{
        companyName,
        addressLine1,
        addressLine2,
        supplierType,
        country,
        state,
        zipcode,
        ...initialValues
      }}
    >
      <FormInput
        name="companyName"
        label={TEXTS.COMPANY_NAME}
        rules={[
          { required: true, whitespace: true, message: ERROR_MESSAGES.companyNameRequired },
          {
            validator: async (_, value) => {
              if (decimalCheck(value?.trim())) {
                return Promise.reject("Company Name cannot be only numbers");
              }
              if (specialCharacterCheck(value.trim())) {
                return Promise.reject("Company Name cannot be only symbols");
              }
              if (!allowedSpecialCharacterCheck(value)) {
                return Promise.reject("Company Name contains invalid characters");
              }
              await validateCompanyNameAsync(_, value);
              return Promise.resolve();
            },
          },
          VALIDATION_RULES.min2,
          VALIDATION_RULES.max100
        ]}
        placeholder={TEXTS.COMPANY_NAME}
        formFieldType="input"
        AutoFocus={true}
        disabled={existingSupplier}
        onChange={(e) => dispatch(setCompanyName(e.target.value))}
        value={companyName}
      />

      <FormInput
        name="addressLine1"
        label={TEXTS.ADDRESSLINE1}
        disabled={isExistingCompany}
        rules={[
          { required: true, whitespace: true, message: ERROR_MESSAGES.addressLine1Required },
          VALIDATION_RULES.max100,
          {
            validator: (_, value) => {
              if (decimalCheck(value.trim())) {
                return Promise.reject("Address cannot be only numbers");
              }
              if (specialCharacterCheck(value.trim())) {
                return Promise.reject("Address cannot be only symbols");
              }
              if (!allowedSpecialCharacterCheck(value)) {
                return Promise.reject("Address contains invalid characters");
              }
          
              return Promise.resolve();

            },
          },
        ]}
        placeholder={TEXTS.ADDRESSLINE1}
        formFieldType="input"
        onChange={(e) => dispatch(setAddressLine1(e.target.value))}
        value={addressLine1}
      />

      <FormInput
        name="addressLine2"
        disabled={isExistingCompany}
        label={TEXTS.ADDRESSLINE2}
        rules={[VALIDATION_RULES.max100, {
          validator: (_, value) => {
            if (decimalCheck(value.trim())) {
              return Promise.reject("Address cannot be only numbers");
            }
            if (specialCharacterCheck(value.trim())) {
              return Promise.reject("Address cannot be only symbols");
            }
            if (!allowedSpecialCharacterCheck(value)) {
              return Promise.reject("Address contains invalid characters");
            }
        
            return Promise.resolve();
          }
          },
        ]}
        placeholder={TEXTS.ADDRESSLINE2}
        formFieldType="input"
        required={false}
        onChange={(e) => dispatch(setAddressLine2(e.target.value))}
        value={addressLine2}
      />

        <FormInput
          name="supplierType"
          label="Supplier Type"
          className="form-item-left-align"
          rules={[VALIDATION_RULES.supplierTypeRequired]}
          placeholder="Supplier Type"
          formFieldType="Select"
          value={supplierType}
          disabled={isExistingCompany || existingSupplier}
          required={true}
          options={[
            {
              key: "Supplier",
              value: "Supplier",
              label: "Supplier",
            },
            {
              key: "Seller",
              value: "Seller",
              label: "Seller",
            }]}
          onChange={(value) => dispatch(setSupplierType(value))}
        />
      <FormInput
        name="country"
        label={TEXTS.COUNTRY}
        disabled={isExistingCompany}
        rules={[{ required: true, message: ERROR_MESSAGES.countryRequired }]}
        placeholder="Select Country"
        formFieldType="Select"
        onChange={handleCountryChange}
        value={country}
        showSearch
        filterOption={(input, option) =>
          option.children?.toLowerCase().includes(input.toLowerCase())
        }
        options={countries.map((country) => ({
          key: country.isoCode,
          value: country.isoCode,
          label: country.name,
        }))}
      />

      <FormInput
        name="state"
        label={TEXTS.STATE}
        rules={[{ required: true, message: ERROR_MESSAGES.stateRequired }]}
        placeholder="Select State"
        formFieldType="Select"
        disabled={!hasStates || isExistingCompany}
        showSearch={hasStates}
        onChange={(value) => dispatch(setState(value))}
        value={state}
        filterOption={(input, option) =>
          option?.children?.toLowerCase().includes(input.toLowerCase())
        }
        options={
          hasStates 
            ? states.map((state) => ({
                key: state.isoCode,
                value: state.isoCode,
                label: state.name,
              }))
            : [{ key: 'NA', value: 'N/A', label: 'N/A' }]
        }
      />

      <FormInput
        name="zipcode"
        disabled={isExistingCompany}
        label={TEXTS.ZIPCODE}
        rules={[
          { required: true, message: ERROR_MESSAGES.zipcodeRequired },
          
          {
            validator: (_, value) => {
              if (!value) {
                return Promise.reject();
              }
              const countrycode = country;
 
              const isValid = postalCodes.validate(countrycode, value);
              if (isValid !== true) {
                return Promise.reject(new Error("Invalid ZIP code for this country"));
              }
              return Promise.resolve();
            },
          },
        ]}
        placeholder={TEXTS.ZIPCODE}
        formFieldType="input"
        onChange={(e) => dispatch(setZipCode(e.target.value))}
        value={zipcode}
      />

      <Button hidden={isExistingCompany} type="primary" htmlType="submit" className="w-[20%]">
        {SUPPLIER_DETAILS_CONSTANTS.BUTTON_ADD}
      </Button>
      {isExistingCompany && 
      <Button  type="primary"onClick={backToLogin}>Go to Login Page</Button>}
      <Button hidden={isExistingCompany} className="ml-2 w-[20%]" onClick={onCancel}>
        {SUPPLIER_DETAILS_CONSTANTS.BUTTON_CANCEL}
      </Button>
    </Form>
  );
};

export default SupplierDetails;
